/**
 * agentBroadcastService.ts
 *
 * Orchestration layer for AI agent live broadcasts (Phase 2 / slice 4c).
 *
 * The hard work lives in two other places:
 *
 *   - `AgentBroadcastCanvas` (slice 4b) owns the synthetic
 *     MediaStream — a Ken-Burns-rendered canvas plus a
 *     MediaStreamAudioDestinationNode we feed TTS clips into.
 *
 *   - The backend's `/api/v2/agents/:agent/speak` (slice 4a) turns a
 *     signed text envelope into rendered audio via Azure Speech.
 *
 * This service is the glue:
 *
 *   - `getSpeechVoices()`           — populate the operator UI dropdown
 *   - `getSpeechStatus()`           — diagnostics + whether TTS is configured
 *   - `pushTestCaption(engine, t)`  — local-only caption (no TTS); used
 *                                     by the preview modal
 *   - `requestSpeechAudio(...)`     — call /speak with an agent-signed
 *                                     envelope and return audio bytes
 *
 * Signing the envelope requires the agent's Ed25519 secret, which we
 * deliberately keep OUT of the browser. So `requestSpeechAudio` is
 * here for completeness (the MCP server uses the matching helper in
 * `mcp-server/src/positionApi.js`-style flows, slice 4d) but most
 * browser callers will use `pushTestCaption` for previewing visuals
 * and let the MCP server drive real captions.
 */

import type {
  AgentBroadcastEngine,
  PushCaptionInput,
} from '../components/AgentBroadcastCanvas';

// ── Types ──────────────────────────────────────────────────────────────────

export interface SpeechVoice {
  id: string;
  label: string;
  default?: boolean;
}

export interface SpeechVoiceCatalogue {
  default: string;
  voices: SpeechVoice[];
}

export interface SpeechStatus {
  configured: boolean;
  sdkAvailable: boolean;
  region: string | null;
  defaultVoice: string;
  maxChars: number;
  voices: string[];
}

export interface SpeechResult {
  voice: string;
  format: string;
  mime: string;
  durationMs: number;
  characters: number;
  textSha256: string;
  audio: Blob;            // ready to feed into engine.pushCaption({ audio })
}

export interface SignedSpeechEnvelope {
  agent: string;
  ts: number;
  nonce: string;
  text_sha256: string;
  voice: string;
}

// ── URL resolution (mirrors agentService) ──────────────────────────────────

function backendBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const isDev =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return isDev
    ? `${window.location.protocol}//${window.location.hostname}:8080`
    : '';
}

function url(path: string): string {
  const base = process.env.REACT_APP_BACKEND_URL || backendBaseUrl();
  return `${base}/api/v2/agents${path}`;
}

async function asJson<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const txt = await resp.text().catch(() => resp.statusText);
    let msg = txt;
    try {
      const parsed = JSON.parse(txt);
      msg = parsed.error || parsed.message || txt;
    } catch { /* leave as-is */ }
    throw new Error(msg || `HTTP ${resp.status}`);
  }
  return (await resp.json()) as T;
}

// ── Public reads ───────────────────────────────────────────────────────────

let _voiceCache: SpeechVoiceCatalogue | null = null;
let _voiceCacheAt = 0;
const VOICE_TTL_MS = 5 * 60 * 1000;

export async function getSpeechVoices(force = false): Promise<SpeechVoiceCatalogue> {
  const now = Date.now();
  if (!force && _voiceCache && now - _voiceCacheAt < VOICE_TTL_MS) {
    return _voiceCache;
  }
  const resp = await fetch(url('/speech/voices'));
  const data = await asJson<{ success: true } & SpeechVoiceCatalogue>(resp);
  _voiceCache = { default: data.default, voices: data.voices };
  _voiceCacheAt = now;
  return _voiceCache;
}

export async function getSpeechStatus(): Promise<SpeechStatus> {
  const resp = await fetch(url('/speech/status'));
  const data = await asJson<{ success: true } & SpeechStatus>(resp);
  return data;
}

// ── Captions ───────────────────────────────────────────────────────────────

/**
 * Push a caption locally — no TTS, no signature, no Azure cost. Useful
 * for the operator preview modal so visual rendering can be validated
 * without burning the speech budget. Returns the engine's caption id.
 */
export async function pushTestCaption(
  engine: AgentBroadcastEngine,
  text: string,
  durationMs?: number,
): Promise<{ id: number; durationMs: number }> {
  const trimmed = String(text || '').trim();
  if (!trimmed) throw new Error('text is required');
  return await engine.pushCaption({ text: trimmed, durationMs });
}

/**
 * Call the backend `/speak` endpoint with a pre-signed envelope and
 * return the rendered audio bytes. Browser callers don't normally
 * have the agent's Ed25519 secret, so this helper is mostly used by
 * the MCP server's HTTP shim (slice 4d) and exposed here for unit
 * tests + future operator-key flows.
 *
 * The envelope must be canonicalized with the same shape the backend
 * verifier expects (see `server/services/agentSignatureVerifier.js`).
 */
export async function requestSpeechAudio(args: {
  agent: string;
  text: string;
  envelope: SignedSpeechEnvelope;
  signatureBase64: string;
}): Promise<SpeechResult> {
  const { agent, text, envelope, signatureBase64 } = args;
  if (!agent || !text) throw new Error('agent and text are required');

  const resp = await fetch(url(`/${encodeURIComponent(agent)}/speak`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      payload:   envelope,
      signature: signatureBase64,
      text,
    }),
  });
  const data = await asJson<{
    success: true;
    voice: string;
    format: string;
    mime: string;
    duration_ms: number;
    characters: number;
    text_sha256: string;
    audio_base64: string;
  }>(resp);

  // Convert base64 -> Blob in a way that works in every browser the
  // bundle targets (no Buffer in the renderer).
  const bin = atob(data.audio_base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const audio = new Blob([bytes], { type: data.mime });

  return {
    voice:      data.voice,
    format:     data.format,
    mime:       data.mime,
    durationMs: data.duration_ms,
    characters: data.characters,
    textSha256: data.text_sha256,
    audio,
  };
}

/**
 * Convenience helper: synthesize via the backend and immediately push
 * the caption + audio to the engine.
 */
export async function speakAndPush(
  engine: AgentBroadcastEngine,
  args: {
    agent: string;
    text: string;
    envelope: SignedSpeechEnvelope;
    signatureBase64: string;
  },
): Promise<{ id: number; durationMs: number; audio: Blob }> {
  const result = await requestSpeechAudio(args);
  const input: PushCaptionInput = {
    text:       args.text,
    audio:      result.audio,
    durationMs: result.durationMs,
  };
  const pushed = await engine.pushCaption(input);
  return { ...pushed, audio: result.audio };
}
